import { html, css, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FormBase } from './formBase';

@customElement('success-form')
export class SuccessForm extends FormBase {
  static styles = css`
    :host {
      display: block;
    }
    
    .success-form-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      min-height: 300px;
    }
    
    .success-form-container.success-bg {
      background-image: url('/static/dsv4.0/images/web/success_form_bg.png');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 16px;
      padding: 24px;
    }
    
    .success-form-container.error-bg {
      background: linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%);
      border-radius: 16px;
      padding: 24px;
    }
    
    .svg-container {
      width: 133px;
      height: 123px;
      margin: 0 auto;
    }
    
    .title {
      font-style: normal;
      font-weight: 600;
      font-size: 28.4444px;
      line-height: 36px;
      margin: 20px 0 8px 0;
    }
    
    .subtitle {
      font-size: 16px;
      line-height: 24px;
      color: #6B7280;
      margin: 0 0 24px 0;
    }
    
    .card-content {
      position: relative;
      z-index: 1;
    }
    
    /* Dark overlay for better text readability on background image */
    .success-bg .title {
      font-style: normal;
      font-weight: 400;
      font-size: 24px;
      line-height: 36px;
    }
    
    .success-bg .subtitle {
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      text-align: center;
    }
    
    @media (max-width: 768px) {
      .success-form-container.success-bg,
      .success-form-container.error-bg {
        padding: 16px;
      }
      
      .svg-container {
        width: 100px;
        height: 92px;
      }
      
      .title {
        font-size: 24px;
        line-height: 32px;
      }
      
      .subtitle {
        font-size: 14px;
        line-height: 20px;
      }
    }
  `;

  @property({ type: Object }) template: any;
  @property({ type: String }) triggered_by: unknown;
  @property({ type: Number }) sectionSize: unknown;
  @property({ type: Function }) _dialog: unknown;
  @property({ type: Array }) oe: unknown;
  @property({ type: String }) title: string = '';
  @property({ type: String }) subtitle: any;
  @property({ type: String }) textAlign: any;
  @property({ type: String }) service: any;
  @property({ type: Array }) pageGroups: unknown;
  @property({ type: String }) currentPageGroupName: unknown;
  @property({ type: Function }) submitForm: unknown;
  @property({ type: Function }) getForm2: unknown;
  @property({ type: Function }) skipForm: unknown;
  @property({ type: Function }) setCurrentPos: any;
  @property({ type: Number }) cuerrentPos: any;
  @property({ type: Function }) setCurrentFeed: any;
  @property({ type: String }) currentFeed: any;
  @property({ type: Array }) feeds: any;
  @property({ type: Function }) setFeeds: any;
  @property({ type: Object }) params: unknown;
  @property({ type: Object })
  details!: { subtitle: string; alignment: string; success: boolean; };

  @state() private subtitleText = '';

  connectedCallback() {
    super.connectedCallback();
    // Initialize subtitle from details
    if (this.details?.subtitle) {
      this.subtitleText = this.details.subtitle;
    }
  }

  updated(changedProperties: PropertyValueMap<any> | Map<string, any>) {
    if (changedProperties.has('details')) {
      this.subtitleText = this.details?.subtitle || '';
    }
  }

  getAlignment() {
    return this.details?.alignment?.toLowerCase() || 'center';
  }

  get isSuccess() {
    return this.details && this.details.success !== false;
  }

  getBackgroundClass() {
    return this.isSuccess ? 'success-bg' : 'error-bg';
  }

  getSuccessSvg() {
    return html`
      <svg width="123" height="123" viewBox="0 0 123 123" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M123 61.5C123 95.4655 95.4655 123 61.5 123C27.5345 123 0 95.4655 0 61.5C0 27.5345 27.5345 0 61.5 0C95.4655 0 123 27.5345 123 61.5ZM5.75046 61.5C5.75046 92.2896 30.7104 117.25 61.5 117.25C92.2896 117.25 117.25 92.2896 117.25 61.5C117.25 30.7104 92.2896 5.75046 61.5 5.75046C30.7104 5.75046 5.75046 30.7104 5.75046 61.5Z" fill="#00A91B"/>
        <path d="M91.0055 41.1924L52.879 79.3188L35.5488 61.9886" stroke="url(#paint0_linear_1467_22492)" stroke-width="7.10983" stroke-linecap="round" stroke-linejoin="round"/>
        <defs>
          <linearGradient id="paint0_linear_1467_22492" x1="32.7049" y1="60.4334" x2="58.6558" y2="70.0316" gradientUnits="userSpaceOnUse">
            <stop stop-color="#00A91B"/>
            <stop offset="1" stop-color="#00A91B"/>
          </linearGradient>
        </defs>
      </svg>
    `;
  }

  getErrorSvg() {
    return html`
      <svg width="131" height="123" viewBox="0 0 131 123" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M94.1277 14.9159C95.6677 12.683 95.1146 9.60553 92.7658 8.24856C84.5538 3.50405 75.3347 0.711639 65.8248 0.119051C54.6768 -0.57561 43.5501 1.7789 33.639 6.92982C23.728 12.0808 15.4068 19.8336 9.56887 29.3562C3.7309 38.8787 0.596521 49.8114 0.502193 60.9806C0.407865 72.1499 3.35715 83.1339 9.03346 92.7537C14.7098 102.374 22.8988 110.266 32.7214 115.583C42.544 120.901 53.6294 123.443 64.7876 122.937C74.3062 122.505 83.5711 119.869 91.8621 115.263C94.2334 113.946 94.8384 110.879 93.3363 108.62C91.8342 106.361 88.7948 105.771 86.4037 107.052C79.6144 110.689 72.078 112.773 64.3423 113.124C54.9664 113.549 45.6516 111.413 37.3979 106.945C29.1441 102.477 22.2631 95.8451 17.4934 87.7618C12.7238 79.6785 10.2455 70.4488 10.3248 61.0636C10.4041 51.6783 13.0378 42.4918 17.9433 34.4903C22.8488 26.4887 29.8409 19.9741 38.1689 15.6459C46.4969 11.3177 55.8465 9.33928 65.2139 9.92299C72.9426 10.4046 80.4426 12.6153 87.1696 16.3668C89.5387 17.6879 92.5876 17.1489 94.1277 14.9159Z" fill="#FA0A0A"/>
        <path d="M120.304 20L46.3042 94" stroke="#FA0A0A" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M105.767 91.503L41.5003 35.1671" stroke="#FA0A0A" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  render() {
    const alignment = this.getAlignment();
    const textAlignClass = alignment === 'left' ? 'text-left' : 
                         alignment === 'right' ? 'text-right' : 'text-center';
    const backgroundClass = this.getBackgroundClass();
    const isSuccess = this.isSuccess;

    return html`
      <div class="success-form-container w-full flex flex-col items-center ${backgroundClass}">
        <!-- SVG centered by default -->
        <div class="svg-container flex items-center justify-center ${textAlignClass === 'text-left' ? 'self-start' : textAlignClass === 'text-right' ? 'self-end' : ''}">
          ${isSuccess ? this.getSuccessSvg() : this.getErrorSvg()}
        </div>
        
        <!-- Title -->
        <p class="title w-full ${textAlignClass}">
          ${this.title}
        </p>
        
        <!-- Subtitle -->
        ${this.subtitleText ? html`
          <p class="subtitle w-full ${textAlignClass}">
            ${this.subtitleText}
          </p>
        ` : ''}
        
        <!-- EList component with no padding -->
        <div class="card-content w-full p-0 mt-6">
          <slot name="body"></slot>
        </div>
      </div>
    `;
  }

  // Event handlers to be passed from parent
  handleCloseModal = () => {
    this.dispatchEvent(new CustomEvent('close-modal', {
      bubbles: true,
      composed: true
    }));
  };

  getFormData = () => {
    this.dispatchEvent(new CustomEvent('get-form-data', {
      bubbles: true,
      composed: true
    }));
  };
}